import React from 'react';
import { Colors, Text } from 'library';
import { Avatar, styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { formatLongWeekdayMonthDayYear, format12HourTime } from 'shared/Dates';
import { RTable, RTableColumn } from 'recruit/components';

import { hoursReported, actions, needsReview } from './utils';

import type { TimesheetWorker, UnclockedInTimesheetWorker } from 'parent-portal/home/new/types';
import { CenterMissingPayment } from 'models';

type TimesheetItem = TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment;
type TimesheetRow = { id: string; item: TimesheetItem };

export function LargeTimesheetsTable({
    data,
    editAction,
}: {
    data: TimesheetWorker[] | UnclockedInTimesheetWorker[] | CenterMissingPayment[];
    editAction: (timesheet: TimesheetItem) => void;
}) {
    const rows: TimesheetRow[] = data.map((t) => ({
        id: `${t.user.id}-${t.start_date}`,
        item: t,
    }));

    const columns: RTableColumn<TimesheetRow>[] = [
        {
            key: 'name',
            label: 'Name',
            render: ({ item }) => (
                <NameCell>
                    <WorkerAvatar src={item.profile_image?.image} />
                    <Link to={`/provider/${item.user.id}`} style={{ paddingRight: 10 }}>
                        <Text variant="body2" textStyle={{ marginLeft: 10, textDecoration: 'underline' }}>
                            {item.user.first_name} {item.user.last_name}
                        </Text>
                    </Link>
                    {needsReview(item)}
                </NameCell>
            ),
        },
        {
            key: 'scheduled',
            label: 'Shift Scheduled',
            render: ({ item }) =>
                item.end_date ? (
                    <>
                        <Text variant="body2">{formatLongWeekdayMonthDayYear(item.start_date, item.timezone)}</Text>
                        <Text variant="body2">
                            {format12HourTime(item.start_date, item.timezone)} -{' '}
                            {format12HourTime(item.end_date, item.timezone, true)}
                        </Text>
                    </>
                ) : (
                    <Text variant="body2">No shift schedule found</Text>
                ),
        },
        {
            key: 'reported',
            label: 'Hours Reported',
            render: ({ item }) => {
                const text = hoursReported(item);
                return (
                    <>
                        {text.middle}
                        {text?.bottom ?? null}
                    </>
                );
            },
        },
        {
            key: 'actions',
            label: 'Actions',
            render: ({ item }) => actions(item, editAction),
        },
    ];

    return <RTable columns={columns} data={rows} />;
}

const NameCell = styled('div')({
    alignItems: 'center',
    display: 'flex',
});

const WorkerAvatar = styled(Avatar)({
    color: Colors.white,
    backgroundColor: Colors.darkNavy,
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'inline-flex',
});
